import React, {useEffect, useState} from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	ListSubheader, Menu,
	MenuItem,
	Typography,
} from "@mui/material";
import {
	ArrowDropDown,
	ArrowDropUp
} from "@mui/icons-material";

interface IModifyReadySelectProps<T, TKey> {
	label?: string;
	fetch: () => Promise<T[]>;
	onChange?: (id: TKey) => void;
	keySelector: keyof T;
	labelSelector: keyof T;
	defaultValue?: TKey;
	modifyContent: (key: TKey) => React.ReactNode;
	createContent: React.ReactNode;
}

export const ModifyReadySelect = <T, TKey,>(props: IModifyReadySelectProps<T, TKey>) => {
	const [records, setRecords] = useState<T[]>([]);
	const [selectedRecord, setSelectedRecord] = useState<TKey | undefined>(props.defaultValue);
	const [isModifyModalOpen, setIsModifyModalOpen] = useState<TKey|undefined>();
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	
	const fetch = () => {
		props.fetch()
			.then(results => setRecords(results));
	};
	const handleChange = (record: T) => {
		setSelectedRecord(record[props.keySelector] as TKey);
		if (props.onChange) {
			props.onChange(record[props.keySelector] as TKey);
		}
		handleClose();
	};
	
	const handleModifyClick = (recordId: TKey) => {
		setIsModifyModalOpen(recordId);
	};
	
	const handleModifyClose = () => {
		setIsModifyModalOpen(undefined);
		fetch();
	};
	
	const handleCreateClick = () => {
		setIsCreateModalOpen(true);
	};
	
	const handleNoneClick = () => {
		setIsModifyModalOpen(undefined);
		setSelectedRecord(undefined as TKey);
		if (props.onChange) {
			props.onChange(undefined as TKey);
		}
		handleClose();
	};
	
	const handleCreateClose = () => {
		setIsCreateModalOpen(false);
		fetch();
	};
	
	const handleOpen = (event: any) => {
		setAnchorEl(event.currentTarget);
	};
	
	const handleClose = () => {
		setAnchorEl(null);
	};
	
	useEffect(() => {
		fetch();
		//eslint-disable-next-line
	}, []);
	
	if (props.defaultValue && records.length === 0) {
		return <>Loading</>;
	}
	
	return (
		<>
			<FormControl fullWidth>
				<Typography
					component="div"
					style={{cursor: "pointer"}}
					role="button"
					onClick={handleOpen}
					sx={{
						border: "1px solid rgba(0, 0, 0, 0.23)",
						borderRadius: "4px",
						padding: "12px 8px 12px 16px",
					}}
				>
					<div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
						<div>
							{selectedRecord !== undefined ? `${records.filter(record => record[props.keySelector] === selectedRecord)[0][props.labelSelector]}` : props.label ?? "Select a record"}
						</div>
						<div style={{display: "flex"}}>
							{anchorEl ? <ArrowDropUp style={{color: "RGB(117, 117, 117)"}} /> : <ArrowDropDown style={{color: "RGB(117, 117, 117)"}} />}
						</div>
					</div>
				</Typography>
				<Menu
					id="custom-select-menu"
					anchorEl={anchorEl}
					open={Boolean(anchorEl)}
					onClose={handleClose}
					MenuListProps={{
						"aria-labelledby": "custom-select-label",
					}}
				>
					<ListSubheader>
						<Button onClick={handleNoneClick} color="secondary">
							None
						</Button>
					</ListSubheader>
					
					{records.map((record) => (
						
						<MenuItem key={`${record[props.keySelector]}`} onClick={() => handleChange(record)}>
							{`${record[props.labelSelector]}`}
							<Button
								onClick={(event) => {
									event.stopPropagation();
									handleModifyClick(record[props.keySelector] as TKey);
								}}
								color="primary"
								sx={{marginLeft: "auto"}}
							>
								Modify
							</Button>
						</MenuItem>
					))}
					<ListSubheader>
						<Button onClick={handleCreateClick} color="secondary">
							Create New Record
						</Button>
					</ListSubheader>
				</Menu>
			</FormControl>
			
			<Dialog
				open={isModifyModalOpen !== undefined}
				onClose={handleModifyClose}
				aria-labelledby="modify-dialog-title"
			>
				<DialogTitle id="modify-dialog-title">Modify Record</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
						{props.modifyContent(isModifyModalOpen!)}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleModifyClose} color="primary">
						Close
					</Button>
				</DialogActions>
			</Dialog>
			
			<Dialog
				open={isCreateModalOpen}
				onClose={handleCreateClose}
				aria-labelledby="create-dialog-title"
			>
				<DialogTitle id="create-dialog-title">Create New Record</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{props.createContent}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCreateClose} color="primary">
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</>

	);
};
